"use client";

import { useRef } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";

interface NavSvgIconProps {
  size?: number;
  className?: string;
  play?: boolean;
}

const CX = 13.08;
const CY = 10.48;
const HALF_GAP = (8 * Math.PI) / 180;
const SIN_HG = Math.sin(HALF_GAP);
const COS_HG = Math.cos(HALF_GAP);

const PULSE_COUNT = 3;
const R_MIN = 2.6;
const R_MAX = 11.5;
const CYCLE_DURATION = 3;

const R_INNER = 6.37;
const R_OUTER = 10.33;

function arcPathAt(r: number) {
  const dx = r * SIN_HG;
  const dy = r * COS_HG;
  const sx = (CX - dx).toFixed(3);
  const ex = (CX + dx).toFixed(3);
  const y = (CY + dy).toFixed(3);
  const rs = r.toFixed(3);
  return `M ${sx} ${y} A ${rs} ${rs} 0 1 1 ${ex} ${y}`;
}

function envelopeOpacity(t: number) {
  if (t < 0.2) return t / 0.2;
  if (t > 0.75) return Math.max(0, 1 - (t - 0.75) / 0.25);
  return 1;
}

export function NewsIcon({ size = 66, className, play = true }: NavSvgIconProps) {
  const containerRef = useRef<SVGSVGElement | null>(null);
  const pulseRefs = useRef<(SVGPathElement | null)[]>(
    Array(PULSE_COUNT).fill(null)
  );

  useGSAP(
    () => {
      if (!play) return;
      const pulses = pulseRefs.current.filter(
        (el): el is SVGPathElement => el !== null
      );
      if (pulses.length !== PULSE_COUNT) return;

      const mm = gsap.matchMedia();
      mm.add("(prefers-reduced-motion: no-preference)", () => {
        const state = { phase: 0 };
        gsap.to(state, {
          phase: 1,
          duration: CYCLE_DURATION,
          repeat: -1,
          ease: "none",
          onUpdate: () => {
            pulses.forEach((p, i) => {
              const t = (state.phase + i / PULSE_COUNT) % 1;
              const r = R_MIN + (R_MAX - R_MIN) * t;
              p.setAttribute("d", arcPathAt(r));
              p.setAttribute("opacity", envelopeOpacity(t).toFixed(3));
            });
          },
        });
      });

      return () => mm.revert();
    },
    { scope: containerRef, dependencies: [play] }
  );

  const initialDs = [arcPathAt(R_INNER), arcPathAt(R_OUTER), arcPathAt(R_MIN)];
  const initialOpacities = ["1", "1", "0"];

  return (
    <svg
      ref={containerRef}
      viewBox="0 0 26.16 26.16"
      width={size}
      height={size}
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="0.3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx={CX} cy={CY} r="2.32" />
      <polygon points="13.08,13.7 11.02,26.01 15.14,26.01" />
      {Array.from({ length: PULSE_COUNT }, (_, i) => (
        <path
          key={i}
          ref={(el) => {
            pulseRefs.current[i] = el;
          }}
          d={initialDs[i]}
          opacity={initialOpacities[i]}
        />
      ))}
    </svg>
  );
}
